/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

/** Renders grouped energy-consumption bars from Building Statistics data. */

/* Import components */
import StoredPlotlyFigure from '@/components/charts/StoredPlotlyFigure';

/* Import libraries */
import { UnitConverter } from '@/lib/unit/UnitConverter';

/* Import types */
import type { BuildingStatisticsData, BuildingStatisticsSupply, BuildingStatisticsSupplyData, StoredPlotlyResponse } from '@/types/eeCheck';
import type { BuildingStatisticsSeriesType } from '@/components/tabs/overview/system/SystemStatisticsTable';

type SystemStatisticsChartSupplyKey = 'pv' | 'grid_feed' | 'grid_supply' | 'heat' | 'water' | 'gas';
export type SystemStatisticsChartMode = 'energy' | 'volume';
export type SystemStatisticsChartSupplyKeys = SystemStatisticsChartSupplyKey[];

interface ChartSupply {
    key: SystemStatisticsChartSupplyKey;
    label: string;
    valueFactor?: number;
}

interface SystemStatisticsChartProps {
    seriesType: BuildingStatisticsSeriesType;
    statistics: BuildingStatisticsData | null;
    targetUnits?: Partial<Record<SystemStatisticsChartSupplyKey, string>>;
    errorMessage?: string;
    isLoading?: boolean;
    mode?: SystemStatisticsChartMode;
    supplyKeys?: SystemStatisticsChartSupplyKeys;
    locale: string;
    t: (key: string) => string;
}

const ENERGY_SUPPLIES: ChartSupply[] = [
    { key: 'pv', label: 'Photovoltaics' },
    { key: 'grid_feed', label: 'Grid feed', valueFactor: -1 },
    { key: 'grid_supply', label: 'Electricity' },
    { key: 'heat', label: 'Heat' },
];

const VOLUME_SUPPLIES: ChartSupply[] = [
    { key: 'water', label: 'Water' },
    { key: 'gas', label: 'Gas' },
];

function isAvailableSupply(supply: BuildingStatisticsSupplyData): supply is BuildingStatisticsSupply {
    return !Array.isArray(supply);
}

function getDisplayUnit(sourceUnit: string, targetUnit: string | undefined): { factor: number; unit: string } {
    if (!targetUnit) {
        return { factor: 1, unit: sourceUnit };
    }

    const factor = new UnitConverter(sourceUnit, targetUnit).getFactor();

    return factor === null ? { factor: 1, unit: sourceUnit } : { factor, unit: targetUnit };
}

function formatTimeValue(timestamp: number, locale: string, seriesType: BuildingStatisticsSeriesType): string {
    if (seriesType === 'yearly_W') {
        return new Intl.DateTimeFormat(locale, {
            timeZone: 'Europe/Berlin',
            year: 'numeric',
        }).format(timestamp * 1000);
    }

    const parts = new Intl.DateTimeFormat(locale, {
        month: '2-digit',
        timeZone: 'Europe/Berlin',
        year: 'numeric',
    }).formatToParts(timestamp * 1000);
    const month = parts.find((part) => part.type === 'month')?.value ?? '';
    const year = parts.find((part) => part.type === 'year')?.value ?? '';

    return `${year}-${month}-01`;
}

function getSupplyPoints(supply: BuildingStatisticsSupply, seriesType: BuildingStatisticsSeriesType) {
    const { timestamps, values } = supply[seriesType];

    return Array.from({ length: Math.min(timestamps.length, values.length) }, (_, index) => ({
        timestamp: timestamps[index],
        value: values[index],
    }))
        .filter(({ timestamp, value }) => Number.isFinite(timestamp) && Number.isFinite(value))
        .sort((left, right) => left.timestamp - right.timestamp);
}

export function getAvailableSystemStatisticsChartSupplyKeys(
    statistics: BuildingStatisticsData | null,
    seriesType: BuildingStatisticsSeriesType,
    mode: SystemStatisticsChartMode,
): SystemStatisticsChartSupplyKeys {
    if (!statistics) {
        return [];
    }

    const supplies = mode === 'energy' ? ENERGY_SUPPLIES : VOLUME_SUPPLIES;

    return supplies.flatMap(({ key }) => {
        const supply = statistics[key];

        return isAvailableSupply(supply) && getSupplyPoints(supply, seriesType).length > 0 ? [key] : [];
    });
}

export function createSystemStatisticsChartFigure({
    locale,
    mode = 'energy',
    seriesType,
    statistics,
    supplyKeys,
    targetUnits,
    t,
}: SystemStatisticsChartProps): StoredPlotlyResponse | null {
    if (!statistics) {
        return null;
    }

    const supplies = (mode === 'energy' ? ENERGY_SUPPLIES : VOLUME_SUPPLIES)
        .filter(({ key }) => supplyKeys?.includes(key) ?? true);
    const data = supplies.flatMap(({ key, label, valueFactor = 1 }) => {
        const supply = statistics[key];

        if (!isAvailableSupply(supply)) {
            return [];
        }

        const { unit } = supply[seriesType];
        const displayUnit = getDisplayUnit(unit, targetUnits?.[key]);
        const points = getSupplyPoints(supply, seriesType);

        if (points.length === 0) {
            return [];
        }

        return [{
            type: 'bar' as const,
            name: `${t(label)} [${displayUnit.unit}]`,
            x: points.map(({ timestamp }) => formatTimeValue(timestamp, locale, seriesType)),
            y: points.map(({ value }) => value * displayUnit.factor * valueFactor),
        }];
    });

    if (data.length === 0) {
        return null;
    }

    return {
        data,
        layout: {
            barmode: 'group',
            legend: { orientation: 'h', y: -0.2 },
            margin: { b: 70, l: 55, r: 20, t: 25 },
            xaxis: seriesType === 'monthly_W'
                ? {
                    dtick: 'M1',
                    tickangle: -45,
                    tickformat: '%m.%Y',
                    title: { text: t('Month') },
                    type: 'date',
                }
                : { title: { text: t('Year') } },
            yaxis: { title: { text: t(mode === 'energy' ? 'Energy' : 'Volume') } },
        },
    };
}

function SystemStatisticsChart(props: SystemStatisticsChartProps) {
    return (
        <StoredPlotlyFigure
            errorMessage={props.errorMessage}
            figure={createSystemStatisticsChartFigure(props)}
            fillParent
            isLoading={props.isLoading}
        />
    );
}

export default SystemStatisticsChart;
